import { TranslateFn } from './order-due';

/**
 * The reservation timer of a cart (design §4.4 / §4.6, mockups Carts + CartDrawer + Dialogs): what the «Бронь» column of
 * the list, the timer card of the drawer and the title of «Бронь заканчивается» print. Pure — the translator is passed in.
 *
 * The server sends SECONDS (`cart.secondsLeft`, counted on its own clock); the browser only subtracts what has elapsed
 * since the answer arrived, on the monotonic clock (`performance.now()`), so neither the browser's clock nor its time zone
 * enter into it. The hosts turn «seconds left at the answer» into `deadline` = `rx + secondsLeft × 1000` once, and the
 * countdown is `deadline − performance.now()` from then on — it does not drift whatever rebuilds the host goes through.
 */

/** The colour of the figures: ≤ 3 minutes is amber, ≤ 1 minute is red (mockup CartDrawer). */
export type CartTone = 'normal' | 'warn' | 'bad';
/** `running` — a figure is counting; `lapsing` — the term has run out and the server has not answered yet; `checking` — an online cart in payment whose payment term has passed. */
export type CartTimerState = 'running' | 'lapsing' | 'checking';

export const CART_WARN_SECONDS = 180;
export const CART_BAD_SECONDS = 60;
/** The reservation's term (`app.cart.expire_seconds`) — the full length of the bar of a reservation. */
export const CART_BOOKING_SECONDS = 600;
/** The payment term of an online cart (`store.payment_timeout`) — the full length of the bar of a cart in payment. */
export const CART_PAYMENT_SECONDS = 1200;

export interface CartTimerView {
  state: CartTimerState;
  /** «8:12» / «5 ч 58 мин» / «≈ 4 мин» / «снимается…» / «проверяется оплата». */
  text: string;
  /** The list's wording: «ещё 8:12» for a running reservation, the same as `text` otherwise. */
  rowText: string;
  tone: CartTone;
  /** How much of the term is left, 0–100 (a sliver stays visible while anything is left). */
  percent: number;
  /** The bar and the figure are drawn: a countdown is on. */
  hasBar: boolean;
}

export function cartTone(seconds: number): CartTone {
  return seconds <= CART_BAD_SECONDS ? 'bad' : seconds <= CART_WARN_SECONDS ? 'warn' : 'normal';
}

/** `deadline` (monotonic ms) from the server's seconds at the moment its answer arrived (`rx`, `performance.now()`). */
export function cartDeadline(rx: number, secondsLeft: number | undefined): number | undefined {
  return secondsLeft === undefined ? undefined : rx + secondsLeft * 1000;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/**
 * `m:ss` below an hour («8:12»), «N ч MM мин» from an hour on («5 ч 58 мин») — a countdown of hours has no use for seconds.
 * The hours form is held together with no-break spaces: in the narrow «Бронь» column the line may wrap before it, never inside it.
 */
export function formatCartClock(seconds: number, tr: TranslateFn): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s >= 3600) {
    return tr('ordersCurrent.cart.timer.hours', { h: Math.floor(s / 3600), m: pad2(Math.floor((s % 3600) / 60)) }).replace(/ /g, '\u00a0');
  }
  return `${Math.floor(s / 60)}:${pad2(s % 60)}`;
}

/** «≈ 4 мин» (the minutes rounded UP: «осталось ≈ 1 мин» until the very last second) or «≈ 1 ч 05 мин». */
function formatApprox(seconds: number, tr: TranslateFn): string {
  const s = Math.max(0, Math.ceil(seconds));
  const time = s >= 3600 ? formatCartClock(s, tr) : tr('ordersCurrent.due.unitM', { n: Math.max(1, Math.ceil(s / 60)) }).replace(/ /g, '\u00a0');
  return tr('ordersCurrent.cart.timer.approx', { time });
}

const NO_TIMER: CartTimerView = { state: 'lapsing', text: '', rowText: '', tone: 'bad', percent: 0, hasBar: false };

/**
 * The timer at one moment. `remainingMs` is the time to the deadline (it may be ≤ 0: the term ran out, the server has not
 * answered yet) or `undefined` when the server sent no `secondsLeft` — for a cart in payment that means «the payment term
 * has passed, the payment is being checked». The figure is rounded UP, so «0:00» is never printed: the last second reads
 * «0:01», then «снимается…» (it never goes below zero).
 */
export function buildCartTimerView(remainingMs: number | undefined, waitPayment: boolean, tr: TranslateFn): CartTimerView {
  const checking = (): CartTimerView => {
    const text = tr('ordersCurrent.cart.timer.checking');
    return { state: 'checking', text, rowText: text, tone: 'normal', percent: 0, hasBar: false };
  };
  if (remainingMs === undefined || remainingMs <= 0) {
    if (waitPayment) {
      return checking();
    }
    const text = tr('ordersCurrent.cart.timer.lapsing');
    return { ...NO_TIMER, text, rowText: text };
  }
  const seconds = Math.ceil(remainingMs / 1000);
  if (waitPayment) {
    const text = formatApprox(seconds, tr);
    return { state: 'running', text, rowText: text, tone: 'normal', percent: share(seconds, CART_PAYMENT_SECONDS), hasBar: true };
  }
  const text = formatCartClock(seconds, tr);
  return {
    state: 'running', text, rowText: tr('ordersCurrent.cart.timer.left', { time: text }),
    tone: cartTone(seconds), percent: share(seconds, CART_BOOKING_SECONDS), hasBar: true,
  };
}

/** The bar: the share of the term that is left, whole percents (the figure changes every second, the bar need not), never an empty bar while anything is left. */
function share(seconds: number, term: number): number {
  return Math.max(2, Math.min(100, Math.round((seconds / term) * 100)));
}
